export interface ExperienceReservationMetadata {
  reservationExpiresAt?: string
  reservationToken?: string
  returnUrl?: string
}

export function getExperienceReservationMetadata(item: App.Checkout.AnyItem): ExperienceReservationMetadata | undefined {
  if (item.itemType !== 'experience') return undefined

  const providerBookingData = item.metaData?.providerBookingData
  if (!providerBookingData || typeof providerBookingData !== 'object') return undefined

  const data = providerBookingData as Record<string, unknown>
  if (typeof data.reservationToken !== 'string' || typeof data.reservationExpiresAt !== 'string') return undefined

  return {
    reservationExpiresAt: data.reservationExpiresAt,
    reservationToken: data.reservationToken,
    returnUrl: typeof data.returnUrl === 'string' ? data.returnUrl : undefined,
  }
}

export function hasExperienceReservationMetadata(item: App.Checkout.AnyItem): boolean {
  return !!getExperienceReservationMetadata(item)
}

export function parseReservationExpiresAt(reservationExpiresAt: string | undefined): number | undefined {
  if (!reservationExpiresAt) return undefined

  const expiresAt = Date.parse(reservationExpiresAt)
  return Number.isFinite(expiresAt) ? expiresAt : undefined
}

export function isExperienceReservationExpired(
  metadata: ExperienceReservationMetadata | undefined,
  now: number = Date.now(),
): boolean {
  const expiresAt = parseReservationExpiresAt(metadata?.reservationExpiresAt)
  return expiresAt !== undefined && expiresAt <= now
}

export function getActiveExperienceReservationExpiresAt(
  item: App.Checkout.AnyItem,
  now: number = Date.now(),
): string | undefined {
  const metadata = getExperienceReservationMetadata(item)
  if (!metadata || isExperienceReservationExpired(metadata, now)) return undefined

  return metadata.reservationExpiresAt
}

export function getEarliestActiveExperienceReservationExpiresAt(
  items: Array<App.Checkout.AnyItem>,
  now: number = Date.now(),
): string | undefined {
  let earliestExpiresAt: string | undefined
  let earliestTimestamp: number | undefined

  items.forEach(item => {
    const reservationExpiresAt = getActiveExperienceReservationExpiresAt(item, now)
    if (!reservationExpiresAt) return

    const expiresAt = parseReservationExpiresAt(reservationExpiresAt)
    if (expiresAt === undefined) return

    if (earliestTimestamp === undefined || expiresAt < earliestTimestamp) {
      earliestExpiresAt = reservationExpiresAt
      earliestTimestamp = expiresAt
    }
  })

  return earliestExpiresAt
}
